"use client"

import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { 
  Shield, ShieldCheck, Users, MoreVertical, ChevronDown, ChevronRight, 
  UserPlus, UserMinus, Settings, AlertCircle, Eye, EyeOff, RefreshCw,
  ArrowUp, ArrowDown, Shuffle, Lock, Unlock, UserCheck, Crown, X
} from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu.jsx'
import { VisuallyHidden } from '../ui/visually-hidden.jsx'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

// Performance optimized Team Roles component
export function TeamRoles() {
  const [hierarchy, setHierarchy] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('tree') // tree, flat, org_chart
  const [selectedRole, setSelectedRole] = useState(null)
  const [showInheritance, setShowInheritance] = useState(false)
  const [expandedRoles, setExpandedRoles] = useState(new Set())
  const [draggedMember, setDraggedMember] = useState(null)
  const [dragOverRole, setDragOverRole] = useState(null)
  const [error, setError] = useState('')
  const [stats, setStats] = useState({ totalRoles: 0, totalMembers: 0, activeRoles: 0 })

  // Load role hierarchy
  const loadHierarchy = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      
      const { data } = await axios.get(`${API}/admin/team/roles/hierarchy?view_mode=${viewMode}`, { 
        withCredentials: true 
      })
      
      setHierarchy(data.hierarchy || [])
      setStats({
        totalRoles: data.total_roles || 0,
        totalMembers: data.total_members || 0,
        activeRoles: data.hierarchy?.filter(r => r.members?.length > 0).length || 0
      })
      
      // Auto-expand roles with members
      const expandedSet = new Set()
      data.hierarchy?.forEach(role => {
        if (role.members?.length > 0) {
          expandedSet.add(role.id)
        }
      })
      setExpandedRoles(expandedSet)
      
    } catch (err) {
      setError('Failed to load role hierarchy')
      console.error('Role hierarchy error:', err)
    } finally {
      setLoading(false)
    }
  }, [viewMode])

  useEffect(() => {
    loadHierarchy()
  }, [loadHierarchy])

  // Toggle role expansion
  const toggleRoleExpansion = (roleId) => {
    const newExpanded = new Set(expandedRoles)
    if (newExpanded.has(roleId)) {
      newExpanded.delete(roleId)
    } else {
      newExpanded.add(roleId)
    }
    setExpandedRoles(newExpanded)
  }

  // Drag and drop handlers
  const handleDragStart = (e, member, sourceRole) => {
    setDraggedMember({ member, sourceRole })
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', member.id)
  }

  const handleDragOver = (e, targetRole) => {
    e.preventDefault()
    setDragOverRole(targetRole.id)
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragLeave = (e) => {
    setDragOverRole(null)
  }

  const handleDrop = async (e, targetRole) => {
    e.preventDefault()
    setDragOverRole(null)
    
    if (!draggedMember || draggedMember.sourceRole.id === targetRole.id) {
      setDraggedMember(null)
      return
    }

    // Show confirmation dialog for role change
    const confirmed = window.confirm(
      `Are you sure you want to move ${draggedMember.member.name} from ${draggedMember.sourceRole.name} to ${targetRole.name}?\n\nThis will change their role and permissions.`
    )

    if (!confirmed) {
      setDraggedMember(null)
      return
    }

    const justification = window.prompt('Please provide a justification for this role change:')
    if (!justification) {
      setDraggedMember(null)
      return
    }

    try {
      await axios.post(
        `${API}/admin/team/roles/${targetRole.id}/assign-member`,
        {
          member_id: draggedMember.member.id,
          justification: justification.trim()
        },
        { withCredentials: true }
      )
      
      // Refresh hierarchy to show changes
      await loadHierarchy()
      alert(`Successfully moved ${draggedMember.member.name} to ${targetRole.name}`)
      
    } catch (err) {
      alert(`Role assignment failed: ${err.response?.data?.detail || err.message}`)
    } finally {
      setDraggedMember(null)
    }
  }

  // Get hierarchy level styling
  const getHierarchyLevelColor = (level) => {
    if (level <= 2) return 'bg-red-50 border-red-200 text-red-800'
    if (level <= 5) return 'bg-orange-50 border-orange-200 text-orange-800'
    if (level <= 7) return 'bg-blue-50 border-blue-200 text-blue-800'
    return 'bg-gray-50 border-gray-200 text-gray-800'
  }

  const getRoleIcon = (roleKey) => {
    switch (roleKey) {
      case 'super_admin': return Crown
      case 'organization_owner': return ShieldCheck
      case 'administrator': return Settings
      case 'support_agent': 
      case 'support_lead': 
      case 'customer_success_manager':
      case 'solutions_architect': return Users
      case 'finance_manager':
      case 'revops_analyst':
      case 'legal_counsel':
      case 'data_protection_officer': return Settings
      case 'sre_platform_engineer':
      case 'devops_engineer':
      case 'backend_engineer':
      case 'frontend_engineer':
      case 'qa_lead':
      case 'release_manager':
      case 'ai_ml_engineer':
      case 'conversation_designer':
      case 'data_engineer': return Settings
      case 'head_of_growth':
      case 'marketing_manager':
      case 'product_manager':
      case 'sales_ae': return UserPlus
      case 'executive_viewer':
      case 'external_auditor': return Eye
      default: return Shield
    }
  }

  return (
    <div className="team-roles mobile-full-width">
      {/* Header with stats and controls */}
      <div className="flex-col-mobile items-start justify-between mb-6 mobile-full-width">
        <div className="w-full mobile-full-width">
          <h2 className="text-2xl font-semibold text-primary mb-2">Role Hierarchy</h2>
          <div className="flex-col-mobile gap-4 text-sm text-muted mobile-full-width">
            <div className="flex gap-4 flex-wrap mobile-full-width">
              <span><Shield size={16} className="inline mr-1" />{stats.totalRoles} Total Roles</span>
              <span><Users size={16} className="inline mr-1" />{stats.totalMembers} Team Members</span>
              <span><UserCheck size={16} className="inline mr-1 text-green-600" />{stats.activeRoles} Active Roles</span>
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 w-full sm:w-auto mobile-full-width">
          <select
            className="input flex-1 sm:min-w-32"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            aria-label="View mode"
          >
            <option value="tree">Tree View</option>
            <option value="flat">Flat List</option>
            <option value="org_chart">Org Chart</option>
          </select>
          
          <button 
            onClick={loadHierarchy} 
            className="btn secondary"
            disabled={loading}
            aria-label="Refresh role hierarchy"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
            <VisuallyHidden>
              {loading ? 'Refreshing role hierarchy data' : 'Reload role hierarchy from server'}
            </VisuallyHidden>
          </button>
          
          <button
            onClick={() => setShowInheritance(!showInheritance)}
            className={`btn ${showInheritance ? 'primary' : 'secondary'}`}
            aria-label="Toggle inheritance view"
          >
            {showInheritance ? <EyeOff size={16} /> : <Eye size={16} />}
            <span className="hidden sm:inline ml-2">
              {showInheritance ? 'Hide' : 'Show'} Inheritance
            </span>
            <VisuallyHidden>
              {showInheritance ? 'Hide permission inheritance information' : 'Show permission inheritance information'}
            </VisuallyHidden>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error mb-4" role="alert">
          {error}
        </div>
      )}

      {/* Instructions */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Shuffle size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-800 mb-1">Drag & Drop Role Assignment</h4>
            <p className="text-sm text-blue-600">
              Drag team members between roles to reassign them. Hierarchy validation and authority checks will be enforced automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Role Hierarchy */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-pulse">Loading role hierarchy...</div>
        </div>
      ) : hierarchy.length === 0 ? (
        <div className="text-center py-8">
          <Shield size={48} className="mx-auto text-muted mb-4" />
          <p className="text-muted">No roles found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {hierarchy.map((role) => (
            <RoleCard
              key={role.id}
              role={role}
              viewMode={viewMode}
              showInheritance={showInheritance}
              expanded={expandedRoles.has(role.id)}
              onToggleExpansion={() => toggleRoleExpansion(role.id)}
              onSelectRole={setSelectedRole}
              selectedRole={selectedRole}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              dragOverRole={dragOverRole}
              getHierarchyLevelColor={getHierarchyLevelColor}
              getRoleIcon={getRoleIcon}
            />
          ))}
        </div>
      )}

      {/* Role Details Modal */}
      {selectedRole && (
        <RoleDetailsModal
          roleId={selectedRole.id}
          onClose={() => setSelectedRole(null)}
        />
      )}
    </div>
  )
}

// Individual Role Card Component
function RoleCard({ 
  role, 
  viewMode, 
  showInheritance, 
  expanded, 
  onToggleExpansion, 
  onSelectRole,
  selectedRole,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  dragOverRole,
  getHierarchyLevelColor,
  getRoleIcon
}) {
  const RoleIcon = getRoleIcon(role.key)
  const hasMembers = role.members && role.members.length > 0
  const isDragOver = dragOverRole === role.id

  return (
    <div 
      className={`card p-4 transition-all duration-200 mobile-full-width ${
        isDragOver ? 'ring-2 ring-blue-500 bg-blue-50' : ''
      } ${selectedRole?.id === role.id ? 'ring-2 ring-primary bg-primary-50' : ''}`}
      onDragOver={(e) => onDragOver(e, role)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, role)}
    >
      {/* Role Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 flex-1">
          <button
            onClick={onToggleExpansion}
            className="icon-btn"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${role.name} role`}
            disabled={!hasMembers}
          >
            {hasMembers ? (
              expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
            ) : (
              <div className="w-4 h-4" />
            )}
          </button>
          
          <div className="flex items-center gap-2">
            <RoleIcon size={20} className="text-primary" />
            <div>
              <h3 className="font-medium text-primary">{role.name}</h3>
              <p className="text-xs text-muted">{role.description}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium px-2 py-1 rounded-full border ${getHierarchyLevelColor(role.hierarchy_level)}`}>
            Level {role.hierarchy_level}
          </span>
          
          <span className="text-xs bg-muted-100 text-muted px-2 py-1 rounded">
            {role.member_count || 0} member{role.member_count !== 1 ? 's' : ''}
          </span>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="icon-btn" aria-label={`Actions for ${role.name} role`}>
                <MoreVertical size={16} aria-hidden="true" />
                <VisuallyHidden>
                  Open actions menu for {role.name} role
                </VisuallyHidden>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onSelectRole(role)}>
                <Eye size={14} className="mr-2" aria-hidden="true" />
                View Details
                <VisuallyHidden>View detailed information for {role.name} role</VisuallyHidden>
              </DropdownMenuItem>
              {role.key !== 'super_admin' && (
                <>
                  <DropdownMenuItem onClick={() => alert('Edit role feature coming soon')}>
                    <Settings size={14} className="mr-2" aria-hidden="true" />
                    Edit Role
                    <VisuallyHidden>Edit {role.name} role configuration</VisuallyHidden>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => alert('Archive role feature coming soon')}
                    className="text-red-600"
                  >
                    <UserMinus size={14} className="mr-2" aria-hidden="true" />
                    Archive Role
                    <VisuallyHidden>Archive {role.name} role (cannot be undone)</VisuallyHidden>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Role Members - Expandable */}
      {hasMembers && expanded && (
        <div className="pl-6 border-l-2 border-muted-200 space-y-2">
          {role.members.map((member) => (
            <MemberItem
              key={member.id}
              member={member}
              role={role}
              onDragStart={onDragStart}
              showInheritance={showInheritance}
            />
          ))}
        </div>
      )}

      {/* Inheritance Information */}
      {showInheritance && (
        <div className="mt-3 pt-3 border-t border-muted-200">
          <div className="text-xs text-muted">
            <span className="font-medium">Inheritance Path:</span>
            {role.inheritance_path && role.inheritance_path.length > 0 ? (
              <span className="ml-2">
                {role.inheritance_path.join(' → ')}
              </span>
            ) : (
              <span className="ml-2 italic">Root level</span>
            )}
          </div>
        </div>
      )}

      {/* Drop Zone Indicator */}
      {isDragOver && (
        <div className="mt-3 p-2 border-2 border-dashed border-blue-500 bg-blue-50 rounded text-center text-blue-600 text-sm">
          Drop here to assign to {role.name}
        </div>
      )}
    </div>
  )
}

// Individual Member Item Component (for drag and drop)
function MemberItem({ member, role, onDragStart, showInheritance }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600'
      case 'invited': return 'text-blue-600'
      case 'deactivated': return 'text-red-600'
      default: return 'text-muted'
    }
  }

  const formatLastActive = (lastActive) => {
    if (!lastActive) return 'Never'
    const date = new Date(lastActive)
    const now = new Date()
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString()
  }

  return (
    <div
      className={`flex items-center gap-3 p-2 rounded hover:bg-muted-50 cursor-move transition-colors mobile-full-width ${
        role.key === 'super_admin' ? 'opacity-75 cursor-not-allowed' : ''
      }`}
      draggable={role.key !== 'super_admin'}
      onDragStart={(e) => role.key !== 'super_admin' && onDragStart(e, member, role)}
      title={role.key === 'super_admin' ? 'Super Admin cannot be moved (Platform Owner)' : 'Drag to move to another role'}
    >
      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
        <span className="text-xs font-medium text-primary">
          {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
        </span>
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-primary text-sm truncate">{member.name}</span>
          {role.key === 'super_admin' && (
            <Crown size={12} className="text-yellow-600" title="Platform Owner" />
          )}
          {member.mfa_enabled && (
            <UserCheck size={12} className="text-green-600" title="MFA Enabled" />
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted">
          <span>{member.email}</span>
          <span className={getStatusColor(member.status)}>
            {member.status}
          </span>
          <span>Last: {formatLastActive(member.last_active)}</span>
        </div>
      </div>

      {role.key === 'super_admin' && (
        <Lock size={14} className="text-yellow-600" title="Protected Account" />
      )}
    </div>
  )
}

// Role Details Modal Component
function RoleDetailsModal({ roleId, onClose }) {
  const [roleDetails, setRoleDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const loadRoleDetails = async () => {
      try {
        setLoading(true)
        setError('')
        
        const { data } = await axios.get(`${API}/admin/team/roles/${roleId}/inheritance`, { 
          withCredentials: true 
        })
        
        setRoleDetails(data)
        
      } catch (err) {
        setError('Failed to load role details')
        console.error('Role details error:', err)
      } finally {
        setLoading(false)
      }
    }
    
    loadRoleDetails()
  }, [roleId])

  if (!roleDetails && !loading) {
    return null
  }

  const groupPermissions = (permissions) => {
    const groups = {}
    permissions.forEach(permission => {
      const module = permission.split(':')[0]
      if (!groups[module]) groups[module] = []
      groups[module].push(permission)
    })
    return groups
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-canvas rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-muted-200">
          <h3 className="text-xl font-semibold text-primary">Role Details</h3>
          <button 
            onClick={onClose}
            className="icon-btn"
            aria-label="Close role details modal"
          >
            <X size={18} aria-hidden="true" />
            <VisuallyHidden>Close role details modal</VisuallyHidden>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-muted">Loading role details...</div>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="error" role="alert">{error}</div>
          </div>
        ) : roleDetails ? (
          <div className="p-6 space-y-6">
            {/* Basic Role Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-medium text-primary mb-3">Role Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-primary mb-1">Name</label>
                      <p className="text-muted">{roleDetails.role?.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary mb-1">Description</label>
                      <p className="text-muted">{roleDetails.role?.description}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary mb-1">Hierarchy Level</label>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary border border-primary-200">
                        Level {roleDetails.role?.hierarchy_level}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-medium text-primary mb-3">Hierarchy</h4>
                  <div className="space-y-3">
                    {roleDetails.parent_chain && roleDetails.parent_chain.length > 0 ? (
                      <div>
                        <label className="block text-sm font-medium text-primary mb-2">Parent Roles</label>
                        <div className="space-y-1">
                          {roleDetails.parent_chain.map((parent, index) => (
                            <div key={parent.id} className="flex items-center gap-2 text-sm">
                              <ArrowUp size={12} className="text-muted" />
                              <span>{parent.name} (Level {parent.hierarchy_level})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted italic">Root level role</p>
                    )}

                    {roleDetails.child_roles && roleDetails.child_roles.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-primary mb-2">Child Roles</label>
                        <div className="space-y-1">
                          {roleDetails.child_roles.map((child) => (
                            <div key={child.id} className="flex items-center gap-2 text-sm">
                              <ArrowDown size={12} className="text-muted" />
                              <span>{child.name} (Level {child.hierarchy_level})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div className="border-t border-muted-200 pt-6">
              <h4 className="text-lg font-medium text-primary mb-4">Permissions</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Direct Permissions */}
                <div>
                  <h5 className="text-sm font-medium text-primary mb-3">
                    Direct Permissions ({roleDetails.direct_permissions?.length || 0})
                  </h5>
                  {roleDetails.direct_permissions && roleDetails.direct_permissions.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {Object.entries(groupPermissions(roleDetails.direct_permissions.map(p => p.key))).map(([module, perms]) => (
                        <div key={module}>
                          <h6 className="text-xs font-medium text-primary uppercase tracking-wide mb-1">
                            {module}
                          </h6>
                          <div className="flex flex-wrap gap-1">
                            {perms.map((perm, index) => (
                              <span key={index} className="text-xs bg-primary-50 text-primary px-2 py-1 rounded border border-primary-200">
                                {perm.split(':')[1]}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted italic">No direct permissions assigned</p>
                  )}
                </div>

                {/* Effective Permissions (with inheritance) */}
                <div>
                  <h5 className="text-sm font-medium text-primary mb-3">
                    Effective Permissions ({roleDetails.effective_permissions?.length || 0})
                  </h5>
                  {roleDetails.effective_permissions && roleDetails.effective_permissions.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {Object.entries(groupPermissions(roleDetails.effective_permissions)).map(([module, perms]) => (
                        <div key={module}>
                          <h6 className="text-xs font-medium text-primary uppercase tracking-wide mb-1">
                            {module}
                          </h6>
                          <div className="flex flex-wrap gap-1">
                            {perms.map((perm, index) => (
                              <span key={index} className="text-xs bg-green-50 text-green-800 px-2 py-1 rounded border border-green-200">
                                {perm.split(':')[1]}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted italic">No effective permissions</p>
                  )}
                </div>
              </div>
            </div>

            {/* Close Actions */}
            <div className="border-t border-muted-200 pt-4">
              <div className="flex justify-end">
                <button 
                  onClick={onClose}
                  className="btn secondary"
                >
                  Close Details
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default TeamRoles